import {
  Breadcrumb,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Table,
  TableColumnType,
  Typography,
  message,
} from 'antd';
import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { useAdminDocumentController_findOneById } from '@api-client/generated/AdminDocumentController/findOneById';
import { useAdminDocumentController_updateOneById } from '@api-client/generated/AdminDocumentController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { IconArrowRight } from '@assets';
import { Card, InfoCell, Loader } from '@components';
import {
  Amount,
  DocumentsStatusTag,
  GoBack,
  StatusProcessBar,
  StatusTag,
  UploadFile,
} from '@entities';
import { useCurrentCompanyId } from '@hooks';
import {
  isPreviewPending,
  PREVIEW_POLL_INTERVAL_MS,
  PreviewableFile,
} from '@utils';

import * as S from './styled';

type Status = Schemas.Document['adminStatus'];

const { Title } = Typography;

const columns: TableColumnType<Schemas.Transaction>[] = [
  {
    key: 'contact',
    dataIndex: 'contact',
    title: 'Contact',
    render: (contact, record) => (
      <S.TransactionLink to={`/transactions/${record.id}`}>
        {contact?.name}
      </S.TransactionLink>
    ),
  },
  {
    key: 'bookingDate',
    dataIndex: 'bookingDate',
    title: 'Booked',
    render: (bookingDate) => format(bookingDate, 'dd.MM.yyyy'),
  },
  {
    key: 'amount',
    dataIndex: 'amount',
    title: 'Amount',
    render: (amount, record) => (
      <Amount amount={amount} currencyCode={record.currencyCode} />
    ),
  },
  {
    key: 'details',
    dataIndex: 'details',
    title: 'Purpose of payment',
  },
  {
    key: 'adminStatus',
    dataIndex: 'adminStatus',
    title: 'Status',
    render: (status) => <StatusTag status={status} />,
  },
];

const DocumentDetails: FC = () => {
  const { id: documentId } = useParams();
  const companyId = useCurrentCompanyId();

  const [statusProcess, setStatusProcess] = useState<Status | null>(null);

  const { data: document, isPending: loading } =
    useAdminDocumentController_findOneById({
      params: {
        id: documentId!,
        companyId: companyId!,
      },
      config: {
        // Poll while the HEIC→JPEG preview is still generating so it appears
        // without a manual reload; stop once it is ready/failed. `query.state.data`
        // is read live, so the decision always uses the latest fetched document.
        refetchInterval: (query) =>
          isPreviewPending(query.state.data) ? PREVIEW_POLL_INTERVAL_MS : false,
      },
    });

  const { mutate: updateFile } = useAdminDocumentController_updateOneById();

  useEffect(() => {
    if (document) {
      setStatusProcess(document.adminStatus);
    }
  }, [document]);

  const handleUpdateDetails = (field: string, value: string) => {
    if (document) {
      updateFile(
        {
          parameter: {
            id: documentId!,
            companyId: companyId!,
          },
          requestBody: {
            referenceId: document.referenceId,
            adminNotes: document.adminNotes,
            [field]: value,
          },
        },
        {
          onSuccess: () => {
            message.success(
              'Document information has been successfully updated'
            );
          },
        }
      );
    }
  };

  const handleUpdateStatus = (status: Status) => {
    handleUpdateDetails('adminStatus', status);
    setStatusProcess(status);
  };

  const handleUpdate = useDebouncedCallback((e) => {
    const { value, id } = e.target;

    handleUpdateDetails(id, value);
  }, 800);

  if (!document || loading) {
    return <Loader />;
  }

  return (
    <S.Container>
      <Flex gap={24} vertical>
        <StatusProcessBar
          status={statusProcess!}
          entityType="file"
          entityId={documentId!}
          onPause={() => handleUpdateStatus('paused')}
          onProcess={() => handleUpdateStatus('processing')}
          onFinish={() => handleUpdateStatus('processed')}
          onRequest={() => handleUpdateStatus('requested_changes')}
        />

        <Breadcrumb
          separator={<IconArrowRight />}
          items={[
            {
              title: <Link to="/documents">Documents</Link>,
            },
            {
              title: document.name,
            },
          ]}
        />

        <S.Header>
          <Flex gap={12} vertical>
            <GoBack />

            <Flex align="center" gap={16}>
              <Title>{document.name}</Title>
              <DocumentsStatusTag
                status={statusProcess!}
                bobStatus={document.bobStatus}
                hasError={document.hasError}
                errorCode={document.errorCode}
                bobPendingAt={document.bobPendingAt}
                // The label may be optimistic — `statusProcess` moves as soon as the operator picks a
                // status — but the error facet reads the persisted row. Deriving it from the optimistic
                // label would make the marker vanish the moment somebody selected another status for a
                // document whose sync had actually failed.
                facetStatus={document.adminStatus}
              />
            </Flex>
          </Flex>
        </S.Header>

        <Flex gap={30} vertical>
          <Form
            layout="vertical"
            onChange={handleUpdate}
            colon={false}
            requiredMark={false}
            initialValues={{
              referenceId: document.referenceId,
              adminNotes: document.adminNotes,
            }}
          >
            <Row gutter={[48, 30]}>
              <Col span={15}>
                <Card title="Info">
                  <Row gutter={[30, 30]}>
                    <Col span={12}>
                      <InfoCell label="Type" value={document.type} />
                    </Col>

                    <Col span={12}>
                      <InfoCell
                        label="Contact"
                        value={document.contact?.name || ''}
                      />
                    </Col>

                    <Col span={24}>
                      <UploadFile
                        url={document.url}
                        name={document.name}
                        type={document.mimeType}
                        previewUrl={
                          (document as PreviewableFile).previewUrl ?? undefined
                        }
                        previewStatus={
                          (document as PreviewableFile).previewStatus ??
                          undefined
                        }
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col span={9}>
                <Form.Item name="referenceId" label="Reference ID">
                  <Input placeholder="Add reference ID" size="large" />
                </Form.Item>

                <S.FormItemClear name="adminNotes" label="Notes">
                  <Input.TextArea
                    placeholder="Add note..."
                    rows={5}
                    size="large"
                  />
                </S.FormItemClear>
              </Col>
            </Row>
          </Form>

          <Card title="Transactions">
            <Table
              rowKey={({ id }) => id}
              dataSource={document.transactions || []}
              columns={columns}
              loading={false}
              pagination={false}
              scroll={{ x: 720 }}
            />
          </Card>
        </Flex>
      </Flex>
    </S.Container>
  );
};

export default DocumentDetails;
